/**
 * Referral request Task — provider capacity requests (PRD_REFERRAL_REQUESTS_FHIR_MAPPING).
 *
 * Persisted as FHIR `Task` with `code` = task-type|referral-request, inputs for preferences,
 * outputs for assigned episodes with assignment-disposition extension.
 */

import type { Reference, Task, TaskInput, TaskOutput } from '@medplum/fhirtypes';

import {
  ASSIGNMENT_ASSIGNED_AT_EXT,
  ASSIGNMENT_DISPOSITION_EXT,
  CANCELLATION_QUEUED_EXT,
  REFERRAL_REQUEST_TASK_CODE,
  RESERVATION_SESSION_EXT,
  TASK_INPUT_SYSTEM,
  TASK_OUTPUT_SYSTEM,
  TASK_TYPE_SYSTEM,
} from '@unify/fhir-model/constants';

export type AssignmentDisposition = 'reserved' | 'pending' | 'converted' | 'declined';

const TASK_INPUT_CODES = {
  numberOfClients: 'number-of-clients',
  acceptsInPerson: 'accepts-in-person',
  noMinors: 'no-minors',
  noCouplesFamilies: 'no-couples-families',
  onlyCashPay: 'only-cash-pay',
} as const;

const ASSIGNED_EPISODE_OUTPUT_CODE = 'assigned-episode';

/** True when Task is a Unify referral-request capacity item. */
export function isReferralRequestTask(task: Task): boolean {
  return Boolean(
    task.code?.coding?.some(
      (c) => c.system === TASK_TYPE_SYSTEM && c.code === REFERRAL_REQUEST_TASK_CODE,
    ),
  );
}

/** Practitioner who submitted the referral request (`Task.requester`, not `owner`). */
export function getReferralRequesterPractitionerId(task: Task): string | undefined {
  const ref = task.requester?.reference;
  if (!ref?.startsWith('Practitioner/')) return undefined;
  const id = ref.replace(/^Practitioner\//, '');
  return id || undefined;
}

/**
 * Practitioner to show on public intake — prefers `requester`, falls back to `owner`
 * when it is a Practitioner ref (legacy tasks before requester was set).
 */
export function getReferralProviderPractitionerIdForIntake(task: Task): string | undefined {
  return (
    getReferralRequesterPractitionerId(task) ??
    (() => {
      const ownerRef = task.owner?.reference;
      if (!ownerRef?.startsWith('Practitioner/')) return undefined;
      const id = ownerRef.replace(/^Practitioner\//, '');
      return id || undefined;
    })()
  );
}

function inputInteger(task: Task, code: string): number | undefined {
  const inp = task.input?.find((i) =>
    i.type?.coding?.some((c) => c.system === TASK_INPUT_SYSTEM && c.code === code),
  );
  return inp?.valueInteger;
}

function inputBoolean(task: Task, code: string): boolean | undefined {
  const inp = task.input?.find((i) =>
    i.type?.coding?.some((c) => c.system === TASK_INPUT_SYSTEM && c.code === code),
  );
  return inp?.valueBoolean;
}

export function getReferralNumberOfClientsRequested(task: Task): number {
  return inputInteger(task, TASK_INPUT_CODES.numberOfClients) ?? 0;
}

export function getReferralAcceptsInPerson(task: Task): boolean | undefined {
  return inputBoolean(task, TASK_INPUT_CODES.acceptsInPerson);
}

export function getReferralNoMinors(task: Task): boolean | undefined {
  return inputBoolean(task, TASK_INPUT_CODES.noMinors);
}

export function getReferralNoCouplesFamilies(task: Task): boolean | undefined {
  return inputBoolean(task, TASK_INPUT_CODES.noCouplesFamilies);
}

export function getReferralOnlyCashPay(task: Task): boolean | undefined {
  return inputBoolean(task, TASK_INPUT_CODES.onlyCashPay);
}

/** Disposition from Task.output.extension[assignment-disposition]. */
export function getOutputAssignmentDisposition(output: TaskOutput): AssignmentDisposition | undefined {
  const ext = output.extension?.find((e) => e.url === ASSIGNMENT_DISPOSITION_EXT)?.valueCode;
  return ext as AssignmentDisposition | undefined;
}

export function getOutputReservationSession(output: TaskOutput): string | undefined {
  return output.extension?.find((e) => e.url === RESERVATION_SESSION_EXT)?.valueString;
}

export function getOutputAssignedAt(output: TaskOutput): string | undefined {
  const ext = output.extension?.find((e) => e.url === ASSIGNMENT_ASSIGNED_AT_EXT);
  return ext?.valueDateTime ?? ext?.valueInstant;
}

export function latestAssignedAt(task: Task): string | undefined {
  let latest: string | undefined;
  for (const output of task.output ?? []) {
    const at = getOutputAssignedAt(output);
    if (!at) continue;
    if (!latest || at > latest) latest = at;
  }
  return latest;
}

export function getCancellationQueued(task: Task): boolean {
  return (
    task.extension?.find((e) => e.url === CANCELLATION_QUEUED_EXT)?.valueBoolean === true
  );
}

/** PRD-computed counts for referral request Task outputs. */
export function computeReferralRequestMetrics(task: Task): {
  referralsReserved: number;
  referralsSent: number;
  referralsConverted: number;
  referralsDeclined: number;
  referralsPending: number;
  availableSlots: number;
} {
  const requested = getReferralNumberOfClientsRequested(task);
  const outputs = task.output ?? [];
  let referralsReserved = 0;
  let referralsConverted = 0;
  let referralsDeclined = 0;
  let referralsPending = 0;

  for (const o of outputs) {
    const isAssigned = o.type?.coding?.some(
      (c) => c.system === TASK_OUTPUT_SYSTEM && c.code === ASSIGNED_EPISODE_OUTPUT_CODE,
    );
    if (!isAssigned) continue;
    const d = getOutputAssignmentDisposition(o);
    if (d === 'reserved') referralsReserved += 1;
    else if (d === 'converted') referralsConverted += 1;
    else if (d === 'declined') referralsDeclined += 1;
    else if (d === 'pending') referralsPending += 1;
  }

  const referralsSent = outputs.filter((o) => {
    const isAssigned = o.type?.coding?.some(
      (c) => c.system === TASK_OUTPUT_SYSTEM && c.code === ASSIGNED_EPISODE_OUTPUT_CODE,
    );
    return isAssigned && getOutputAssignmentDisposition(o) !== 'reserved';
  }).length;

  const availableSlots = Math.max(
    0,
    requested - referralsReserved - referralsPending - referralsConverted,
  );

  return {
    referralsReserved,
    referralsSent,
    referralsConverted,
    referralsDeclined,
    referralsPending,
    availableSlots,
  };
}

/** Map Task.status to provider-facing label buckets. */
export function referralTaskStatusToUiLabel(
  task: Task,
): 'Pending' | 'In Progress' | 'Completed' | 'Cancelled' {
  const s = task.status;
  if (s === 'cancelled') return 'Cancelled';
  if (s === 'completed') return 'Completed';
  const { referralsConverted, referralsDeclined, referralsPending, referralsReserved } =
    computeReferralRequestMetrics(task);
  const inFlight = referralsConverted + referralsDeclined + referralsPending + referralsReserved;
  if (s === 'requested' && inFlight === 0) return 'Pending';
  return 'In Progress';
}

export function buildReferralRequestTaskInputs(opts: {
  numberOfClients: number;
  acceptsInPerson?: boolean;
  noMinors?: boolean;
  noCouplesFamilies?: boolean;
  onlyCashPay?: boolean;
}): TaskInput[] {
  const inputs: TaskInput[] = [
    {
      type: {
        coding: [{ system: TASK_INPUT_SYSTEM, code: TASK_INPUT_CODES.numberOfClients }],
      },
      valueInteger: opts.numberOfClients,
    },
  ];
  if (opts.acceptsInPerson != null) {
    inputs.push({
      type: { coding: [{ system: TASK_INPUT_SYSTEM, code: TASK_INPUT_CODES.acceptsInPerson }] },
      valueBoolean: opts.acceptsInPerson,
    });
  }
  if (opts.noMinors != null) {
    inputs.push({
      type: { coding: [{ system: TASK_INPUT_SYSTEM, code: TASK_INPUT_CODES.noMinors }] },
      valueBoolean: opts.noMinors,
    });
  }
  if (opts.noCouplesFamilies != null) {
    inputs.push({
      type: { coding: [{ system: TASK_INPUT_SYSTEM, code: TASK_INPUT_CODES.noCouplesFamilies }] },
      valueBoolean: opts.noCouplesFamilies,
    });
  }
  if (opts.onlyCashPay != null) {
    inputs.push({
      type: { coding: [{ system: TASK_INPUT_SYSTEM, code: TASK_INPUT_CODES.onlyCashPay }] },
      valueBoolean: opts.onlyCashPay,
    });
  }
  return inputs;
}

export function buildReferralRequestTaskCode(): Task['code'] {
  return {
    coding: [
      {
        system: TASK_TYPE_SYSTEM,
        code: REFERRAL_REQUEST_TASK_CODE,
        display: 'Referral Request',
      },
    ],
  };
}

/** Episode ref from an assigned-episode output. */
export function getOutputEpisodeRef(output: TaskOutput): Reference | undefined {
  return output.valueReference;
}
